import { ConstrainMode, IColumn, IDetailsRowProps, IObjectWithKey } from '@fluentui/react';
import { FileIconType, getFileTypeIconProps } from '@fluentui/react-file-type-icons';
import {
  IPIcon,
  IPLink,
  IPScrollablePane,
  IPShimmeredDetailsList,
  IPStack,
  IPText,
  IPTooltipHost,
} from 'baseComponents';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPSelection, IPSelectionMode } from 'baseComponents/Utils/Selection';
import { onRenderDetailsHeaderGlobal } from 'globalFunctions';
import ResourceLink from 'models/resourceLink';
import { MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toLocaleDateNumeric } from 'utils/datetime';
import { sortOnNumber, sortOnString } from 'utils/sorting';
import { bytesToSize } from 'utils/string';
import { IDriveItem } from '../../services/Graph/SharepointInterfaces';

interface IMyDriveItems {
  driveItems: IDriveItem[];
  setDriveItems?: (items: IDriveItem[]) => void;
  onSelectFolder: (item: IDriveItem, isChild: boolean) => void;
  onSelectItems: (items: IDriveItem[] | undefined) => void;
  onOpenFile?: (item: IDriveItem) => void;
  isLoading: boolean;
  layoutMode: IPDetailsListLayoutMode;
  viewMode?: boolean;
  existingLinks?: ResourceLink[];
  linkToEdit?: ResourceLink;
  singleSelect?: boolean;
  disabled?: boolean;
  selectFolderMode?: boolean;
}

const iconSize = 20;
const sortableColumnKeys = new Set(['name', 'changed', 'changedBy', 'size']);

const MyDriveItems = (props: IMyDriveItems) => {
  const { t } = useTranslation(['sharepoint']);
  const [sortedColumnKey, setSortedColumnKey] = useState<string | undefined>(undefined);
  const [isSortedDescending, setIsSortedDescending] = useState(false);

  const handleColumnClick = useCallback(
    (_: MouseEvent<HTMLElement> | undefined, column?: IColumn): void => {
      if (!column || !sortableColumnKeys.has(column.key) || !props.setDriveItems) return;

      const getSortResult = (itemA: IDriveItem, itemB: IDriveItem, columnKey: string): number => {
        switch (columnKey) {
          case 'name':
            return sortOnString(itemA.name?.toLowerCase(), itemB.name?.toLowerCase());
          case 'changed':
            return sortOnNumber(
              itemA.lastModifiedDateTime ? new Date(itemA.lastModifiedDateTime).getTime() : undefined,
              itemB.lastModifiedDateTime ? new Date(itemB.lastModifiedDateTime).getTime() : undefined,
            );
          case 'changedBy':
            return sortOnString(
              itemA.lastModifiedBy?.user?.displayName?.toLowerCase(),
              itemB.lastModifiedBy?.user?.displayName?.toLowerCase(),
            );
          case 'size':
            return sortOnNumber(
              itemA.folder ? undefined : itemA.size ?? undefined,
              itemB.folder ? undefined : itemB.size ?? undefined,
            );
          default:
            return 0;
        }
      };

      const nextIsSortedDescending = sortedColumnKey === column.key ? !isSortedDescending : false;
      const sortedItems = [...props.driveItems].sort((itemA, itemB) => {
        const result = getSortResult(itemA, itemB, column.key);

        return nextIsSortedDescending ? result * -1 : result;
      });

      props.setDriveItems(sortedItems);
      setSortedColumnKey(column.key);
      setIsSortedDescending(nextIsSortedDescending);
    },
    [isSortedDescending, props, sortedColumnKey],
  );

  const columns: IColumn[] = useMemo(
    () => [
      {
        key: 'type',
        minWidth: 20,
        maxWidth: 20,
        name: '',
        onRender: (item?: IDriveItem, index?: number | undefined, column?: IColumn | undefined) => {
          if (!item || !item.name) return;

          if (item.folder) return <IPIcon {...getFileTypeIconProps({ size: iconSize, type: FileIconType.folder })} />;

          const splits = item.name?.split('.');
          const extension = splits[splits?.length - 1];

          return <IPIcon {...getFileTypeIconProps({ extension: extension, size: iconSize })} />;
        },
      },
      {
        key: 'name',
        minWidth: 200,
        isResizable: true,
        name: t('sharepoint:DriveItemPicker.Columns.Name'),
        isSorted: sortedColumnKey === 'name',
        isSortedDescending: sortedColumnKey === 'name' ? isSortedDescending : undefined,
        onColumnClick: handleColumnClick,
        onRender: (item?: IDriveItem, index?: number | undefined, column?: IColumn | undefined) => {
          if (!item) return;

          if (item.folder)
            return (
              <IPText>
                <IPLink disabled={props.disabled} underline onClick={() => props.onSelectFolder(item, true)}>
                  {item.name}
                </IPLink>
              </IPText>
            );

          if (props.viewMode && props.onOpenFile && item.webUrl)
            return (
              <IPText>
                <IPLink
                  disabled={props.disabled}
                  underline
                  onClick={() => {
                    if (props.onOpenFile) props.onOpenFile(item);
                  }}
                >
                  {item.name}
                </IPLink>
              </IPText>
            );

          return <IPText>{item.name}</IPText>;
        },
      },
      {
        key: 'changed',
        minWidth: 90,
        isResizable: true,
        name: t('sharepoint:DriveItemPicker.Columns.Changed'),
        isSorted: sortedColumnKey === 'changed',
        isSortedDescending: sortedColumnKey === 'changed' ? isSortedDescending : undefined,
        onColumnClick: handleColumnClick,
        onRender: (item?: IDriveItem, index?: number | undefined, column?: IColumn | undefined) => {
          if (!item || item.lastModifiedDateTime === undefined) return;

          return <IPText>{toLocaleDateNumeric(new Date(item.lastModifiedDateTime))}</IPText>;
        },
      },
      {
        key: 'changedBy',
        minWidth: 110,
        isResizable: true,
        name: t('sharepoint:DriveItemPicker.Columns.ChangedBy'),
        isSorted: sortedColumnKey === 'changedBy',
        isSortedDescending: sortedColumnKey === 'changedBy' ? isSortedDescending : undefined,
        onColumnClick: handleColumnClick,
        onRender: (item?: IDriveItem, index?: number | undefined, column?: IColumn | undefined) => {
          if (!item) return;

          return <IPText>{item.lastModifiedBy?.user?.displayName}</IPText>;
        },
      },
      {
        key: 'size',
        minWidth: 80,
        isResizable: true,
        name: t('sharepoint:DriveItemPicker.Columns.Size'),
        isSorted: sortedColumnKey === 'size',
        isSortedDescending: sortedColumnKey === 'size' ? isSortedDescending : undefined,
        onColumnClick: handleColumnClick,
        onRender: (item?: IDriveItem, index?: number | undefined, column?: IColumn | undefined) => {
          if (!item || item.folder || !item.size) return;

          return <IPText>{bytesToSize(item.size)}</IPText>;
        },
      },
    ],
    [t, props, sortedColumnKey, isSortedDescending, handleColumnClick],
  );

  const propsRef = useRef(props);
  propsRef.current = props;

  const selection = useMemo(() => {
    const sel: IPSelection<IDriveItem> = new IPSelection<IDriveItem>({
      canSelectItem: (item) => {
        const p = propsRef.current;
        if (p.disabled || item.id === undefined) return false;
        if (p.selectFolderMode) return !!item.folder; // folder-pick mode: ONLY folders selectable
        
return (
          !item.folder &&
          !p.existingLinks?.find(
            (existingLink) =>
              p.linkToEdit?.linkId !== existingLink.linkId &&
              (existingLink.driveItemId === item.id ||
                existingLink.linkName.toLowerCase() === item.name?.toLowerCase()),
          )
        );
      },
      getKey: (item) => item.id || '',
      onSelectionChanged: () => {
        propsRef.current.onSelectItems(sel.getSelection() as IDriveItem[]);
      },
    });

    return sel;
     
  }, []);

  const onRenderRow = (
    rowProps?: IDetailsRowProps,
    defaultRender?: (rowProps?: IDetailsRowProps) => JSX.Element | null,
  ): JSX.Element | null => {
    if (
      !props.selectFolderMode &&
      defaultRender &&
      rowProps?.item &&
      !selection.canSelectItem(rowProps.item) &&
      !rowProps.item.folder
    ) {
      rowProps.disabled = true;

      return (
        <IPTooltipHost
          content={t('sharepoint:SharepointPicker.ItemExists')}
          directionalHint={IPDirectionalHint.topLeftEdge}
        >
          {defaultRender(rowProps)}
        </IPTooltipHost>
      );
    }

    if (defaultRender) {
      return defaultRender(rowProps);
    } else {
      return null;
    }
  };

  //
  // Main render
  //
  if (!props.isLoading && props.driveItems.length === 0) {
    return (
      <IPStack verticalFill verticalAlign="center" horizontalAlign="center">
        <IPStack.Item>
          <IPText>{t('translation:General.Notifications.NoItems')}</IPText>
        </IPStack.Item>
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill>
      <IPStack.Item grow styles={{ root: { position: 'relative' } }}>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          <IPShimmeredDetailsList
            selectionPreservedOnEmptyClick={true}
            columns={columns}
            compact
            selection={selection as IPSelection<IObjectWithKey>}
            layoutMode={props.layoutMode}
            constrainMode={ConstrainMode.unconstrained}
            items={props.driveItems}
            enableShimmer={props.isLoading}
            shimmerLines={3}
            selectionMode={
              props.selectFolderMode
                ? IPSelectionMode.single
                : props.viewMode
                  ? props.singleSelect
                    ? IPSelectionMode.single
                    : IPSelectionMode.none
                  : IPSelectionMode.multiple
            }
            onRenderDetailsHeader={onRenderDetailsHeaderGlobal}
            onRenderRow={onRenderRow}
          />
        </IPScrollablePane>
      </IPStack.Item>
    </IPStack>
  );
};

export default MyDriveItems;
